import { useLayoutEffect, useRef, useState } from 'react';

import { fontStyles } from '@/styles/typography';

import { LABEL_PADDING_VERTICAL } from './constants';
import type { ChartPlot } from './types';

const LABEL_HEIGHT = 20;
const LABEL_OFFSET_X = 7;
const LABEL_WIDTH_DEFAULT = 48;

type ChartCrosshairAxisLabelProps = {
  axis: 'x' | 'y';
  label: string;
  plot: ChartPlot;
  position: number;
  fill?: string;
  textFill?: string;
};

export const ChartCrosshairAxisLabel = ({
  axis,
  label,
  plot,
  position,
  fill = 'var(--color-token-gray-500)',
  textFill = 'var(--color-token-gray-1600)',
}: ChartCrosshairAxisLabelProps) => {
  const [width, setWidth] = useState(LABEL_WIDTH_DEFAULT);
  const textRef = useRef<SVGTextElement | null>(null);

  useLayoutEffect(() => {
    const textWidth = textRef.current?.getBBox().width;
    if (!textWidth) return;

    const nextWidth = Math.ceil(textWidth) + LABEL_PADDING_VERTICAL;
    setWidth((currentWidth) => (currentWidth === nextWidth ? currentWidth : nextWidth));
  }, [label]);

  const maxX = Math.max(plot.left, plot.right - width);

  const x =
    axis === 'x'
      ? Math.min(Math.max(position - width / 2, plot.left), maxX)
      : plot.right + LABEL_PADDING_VERTICAL / 2;

  const y = axis === 'x' ? plot.bottom + LABEL_OFFSET_X : position - LABEL_HEIGHT / 2;

  return (
    <g data-testid={`chart-crosshair-${axis}-label`} aria-hidden="true">
      <rect x={x} y={y} width={width} height={LABEL_HEIGHT} rx={4} fill={fill} />
      <text
        ref={textRef}
        x={x + width / 2}
        y={y + LABEL_HEIGHT / 2}
        dominantBaseline="central"
        textAnchor="middle"
        className={fontStyles({ variant: 'CAPTION' })}
        fill={textFill}
      >
        {label}
      </text>
    </g>
  );
};
